import * as React from "react";
import { useTranslation } from "react-i18next";
import QRCode from "react-qr-code";

import { Logo } from "components/logo/Logo";
import style from "components/reports/passports/passports-view.scss";
import { PassportReport } from "services/report/passports/PassportService";
import { truncateWithEllipsis } from "utils/format";
import { useIsDarkThemeActive, useTheme } from "utils/useTheme";

const LOGO_HEIGHT = 31;
const LOGO_WIDTH = Math.round((LOGO_HEIGHT * 4940) / 1326);
const LOGO_TOP = 24;

// The artefacts have the proportions of the label printers' 62 x 29 mm labels.
const ARTIFACT_WIDTH = 732;
const ARTIFACT_HEIGHT = 343;
const ARTIFACT_DISPLAY_WIDTH_PIXELS = 600;
export const ARTIFACT_DISPLAY_HEIGHT_PIXELS = Math.round(
    (ARTIFACT_DISPLAY_WIDTH_PIXELS * ARTIFACT_HEIGHT) / ARTIFACT_WIDTH
);

const MARGIN = 32;
const CORNER_RADIUS = 8;
// The border is drawn on the edge of the card, so its line is inset by half its width to stay inside the card.
const BORDER_INSET = 0.5;
// The branded header of the image frames the logo with the same space above and below it.
const BRANDED_HEADER_HEIGHT = 2 * LOGO_TOP + LOGO_HEIGHT;
const BRANDED_TITLE_FONT_SIZE = 17;
const LATO_CAP_HEIGHT_RATIO = 0.72;
const QR_CODE_SIZE = 285;
const QR_CODE_LEFT = 417;
const QR_CODE_TOP = 29;
// The branded header leaves less room, so the code is smaller and centered below the header.
const BRANDED_QR_CODE_SIZE = 230;
const BRANDED_QR_CODE_TOP =
    BRANDED_HEADER_HEIGHT + (ARTIFACT_HEIGHT - BRANDED_HEADER_HEIGHT - BRANDED_QR_CODE_SIZE) / 2;

// The tops are measured from the design.
const MANUFACTURER_TOP = 84;
const MODEL_TOP = 114;
const FIRST_FIELD_TOP = 164;
const FIELD_DISTANCE = 82;
const FIELD_VALUE_DISTANCE = 30;
const LABEL_FONT_SIZE = 20;
const LABEL_LINE_HEIGHT = 30;
const VALUE_FONT_SIZE = 28;
const VALUE_LINE_HEIGHT = 37;
const FONT_WEIGHT = "500";
const MODEL_MAX_WIDTH = QR_CODE_LEFT - 2 * MARGIN;
// The model has the size of the other values, so a model that doesn't fit is cut instead of made smaller. Medium
// weight text is wider than the average, so the estimate of the characters that fit is on the safe side.
const AVERAGE_GLYPH_WIDTH_RATIO = 0.58;
const MAX_MODEL_CHARACTERS = Math.floor(MODEL_MAX_WIDTH / (VALUE_FONT_SIZE * AVERAGE_GLYPH_WIDTH_RATIO));
// Lato's ascent and the content height of its line box, as portions of the font size.
const LATO_ASCENT_RATIO = 0.987;
const LATO_CONTENT_HEIGHT_RATIO = 1.2;

const WHITE = "#ffffff";
const BLACK = "#000000";
export const FONT_FAMILY = "Lato, Arial, Helvetica, sans-serif";

export const useArtifactColors = () => {
    const theme = useTheme();
    const isDarkTheme = useIsDarkThemeActive();
    return {
        isDarkTheme,
        // Full black is for the light background only. On a dark one it would disappear.
        text: isDarkTheme ? theme.textColor : BLACK,
        border: theme.cardBorderColor,
        brand: theme.primaryButtonBackgroundColor,
        background: theme.contentBackgroundColor,
    };
};

export interface ReportArtifactProps {
    shareUrl: string;
    report: PassportReport;
}

// SVG text is positioned by its baseline, while the design gives the top of the line box.
const baselineY = (top: number, fontSize: number, lineHeight: number) =>
    top + (lineHeight - LATO_CONTENT_HEIGHT_RATIO * fontSize) / 2 + LATO_ASCENT_RATIO * fontSize;

interface FieldProps {
    top: number;
    label: string;
    value: string;
}

const PassportField = (props: FieldProps) => {
    const colors = useArtifactColors();
    return (
        <g fontFamily={FONT_FAMILY}>
            <text
                x={MARGIN}
                y={baselineY(props.top, LABEL_FONT_SIZE, LABEL_LINE_HEIGHT)}
                fontSize={LABEL_FONT_SIZE}
                fontWeight={FONT_WEIGHT}
                fill={colors.text}
            >
                {props.label.toUpperCase()}
            </text>
            <text
                x={MARGIN}
                y={baselineY(props.top + FIELD_VALUE_DISTANCE, VALUE_FONT_SIZE, VALUE_LINE_HEIGHT)}
                fontSize={VALUE_FONT_SIZE}
                fontWeight={FONT_WEIGHT}
                fill={colors.text}
            >
                {props.value}
            </text>
        </g>
    );
};

interface PassportArtifactCardProps extends ReportArtifactProps {
    hasBrandedHeader: boolean;
}

export const PassportArtifactCard = (props: PassportArtifactCardProps) => {
    const { t } = useTranslation();
    const colors = useArtifactColors();
    const { device_information } = props.report;
    const rightEdge = ARTIFACT_WIDTH - MARGIN;
    const qrCodeSize = props.hasBrandedHeader ? BRANDED_QR_CODE_SIZE : QR_CODE_SIZE;
    const qrCodeLeft = props.hasBrandedHeader ? rightEdge - qrCodeSize : QR_CODE_LEFT;
    const qrCodeTop = props.hasBrandedHeader ? BRANDED_QR_CODE_TOP : QR_CODE_TOP;
    const fields = [
        { label: t("DevicePassport.panel.deviceInformation.imei"), value: device_information?.imei },
        { label: t("DevicePassport.panel.deviceInformation.serial"), value: device_information?.serial },
    ].filter((field): field is { label: string; value: string } => Boolean(field.value));
    return (
        <svg
            className={style.artifact}
            width={ARTIFACT_DISPLAY_WIDTH_PIXELS}
            height={ARTIFACT_DISPLAY_HEIGHT_PIXELS}
            viewBox={`0 0 ${ARTIFACT_WIDTH} ${ARTIFACT_HEIGHT}`}
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
        >
            <rect
                x={BORDER_INSET}
                y={BORDER_INSET}
                width={ARTIFACT_WIDTH - 2 * BORDER_INSET}
                height={ARTIFACT_HEIGHT - 2 * BORDER_INSET}
                rx={CORNER_RADIUS - BORDER_INSET}
                fill={colors.background}
                stroke={colors.border}
            />
            {props.hasBrandedHeader && (
                <>
                    <path
                        d={`M0 ${CORNER_RADIUS}A${CORNER_RADIUS} ${CORNER_RADIUS} 0 0 1 ${CORNER_RADIUS} 0H${ARTIFACT_WIDTH - CORNER_RADIUS}A${CORNER_RADIUS} ${CORNER_RADIUS} 0 0 1 ${ARTIFACT_WIDTH} ${CORNER_RADIUS}V${BRANDED_HEADER_HEIGHT}H0Z`}
                        fill={colors.brand}
                    />
                    <text
                        x={rightEdge}
                        y={BRANDED_HEADER_HEIGHT / 2 + (LATO_CAP_HEIGHT_RATIO * BRANDED_TITLE_FONT_SIZE) / 2}
                        textAnchor="end"
                        fontFamily={FONT_FAMILY}
                        fontSize={BRANDED_TITLE_FONT_SIZE}
                        fill={WHITE}
                    >
                        {t("DevicePassport.artifact.title").toUpperCase()}
                    </text>
                </>
            )}
            <g transform={`translate(${MARGIN} ${LOGO_TOP})`}>
                <Logo
                    isWhiteLogo={props.hasBrandedHeader || colors.isDarkTheme}
                    width={LOGO_WIDTH}
                    height={LOGO_HEIGHT}
                />
            </g>
            {device_information?.manufacturer && (
                <text
                    x={MARGIN}
                    y={baselineY(MANUFACTURER_TOP, LABEL_FONT_SIZE, LABEL_LINE_HEIGHT)}
                    fontFamily={FONT_FAMILY}
                    fontSize={LABEL_FONT_SIZE}
                    fontWeight={FONT_WEIGHT}
                    fill={colors.text}
                >
                    {device_information.manufacturer.toUpperCase()}
                </text>
            )}
            <text
                x={MARGIN}
                y={baselineY(MODEL_TOP, VALUE_FONT_SIZE, VALUE_LINE_HEIGHT)}
                fontFamily={FONT_FAMILY}
                fontSize={VALUE_FONT_SIZE}
                fontWeight={FONT_WEIGHT}
                fill={colors.text}
            >
                {truncateWithEllipsis(device_information?.model ?? "", MAX_MODEL_CHARACTERS)}
            </text>
            {fields.map((field, index) => (
                <PassportField
                    key={field.label}
                    top={FIRST_FIELD_TOP + index * FIELD_DISTANCE}
                    label={field.label}
                    value={field.value}
                />
            ))}
            <g transform={`translate(${qrCodeLeft} ${qrCodeTop})`}>
                <QRCode value={props.shareUrl} size={qrCodeSize} />
            </g>
        </svg>
    );
};
